import { ReplicationChannel } from './channel'
import {
	holdPresence,
	Locks,
	presenceLockName,
	trackPeer,
} from './peer-presence'
import {
	Address,
	openHostMessage,
	RelayedAction,
	RemoteSlices,
	ReplicaBody,
	wrap,
} from './protocol'
import {
	INITIAL_SEQUENCER,
	sequence,
	SequencerState,
} from './replica-sequencer'

type Options = {
	channel: ReplicationChannel
	address: Address
	/** `navigator.locks`; without it the host is never taken for gone. */
	locks?: Locks
	/** Apply slices from a snapshot or a patch to this window's store. */
	applySlices: (slices: RemoteSlices, kind: 'snapshot' | 'patch') => void
	/** Dispatch actions the host relayed, as they are. */
	applyActions: (actions: RelayedAction[]) => void
	/** An application message from the host. */
	onMessage: (payload: unknown) => void
	/** The host appeared (`true`) or went (`false`). */
	onPeer: (present: boolean) => void
	onOtherBuild: () => void
}

export type Replica = {
	/** Sends an application message to the host. */
	post: (payload: unknown) => void
	stop: () => void
}

/**
 * The replica's end of a link. Once this window holds its presence lock it
 * says hello, takes the snapshot, then applies patches and relayed actions in
 * the order `sequence` allows.
 */
export const startReplica = ({
	channel,
	address,
	locks,
	applySlices,
	applyActions,
	onMessage,
	onPeer,
	onOtherBuild,
}: Options): Replica => {
	const peer = trackPeer(locks, presenceLockName(address.link, 'host'), onPeer)
	let sequencer: SequencerState = INITIAL_SEQUENCER
	let speaking = false
	let stopped = false
	let unsubscribe = (): void => undefined

	const send = (body: ReplicaBody): void => channel.post(wrap(address, body))

	const presence = holdPresence(
		locks,
		presenceLockName(address.link, 'replica'),
		() => {
			if (stopped) {
				return
			}
			speaking = true
			unsubscribe = channel.subscribe(data => {
				const message = openHostMessage(data, address)
				if (!message.ok) {
					if (message.reason === 'other-build') {
						onOtherBuild()
					}
					return
				}
				peer.heard()
				const body = message.body
				if (body.kind === 'app') {
					onMessage(body.payload)
					return
				}
				const [next, step] = sequence(sequencer, body)
				sequencer = next
				switch (step) {
					case 'apply':
						if (body.kind === 'actions') {
							applyActions(body.actions)
						} else {
							applySlices(body.slices, body.kind)
						}
						return
					case 'resync':
						send({ kind: 'resync' })
						return
					case 'ignore':
						return
				}
			})
			send({ kind: 'hello' })
		}
	)

	return {
		post: payload => {
			if (speaking && !stopped) {
				send({ kind: 'app', payload })
			}
		},
		stop: () => {
			stopped = true
			unsubscribe()
			peer.stop()
			presence.stop()
		},
	}
}
